import { ArrowLeft, CheckCircle, Eye, Loader2, Redo2, Undo2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { useSaveWorkflow } from '../api/useSaveWorkflow';
import { useWorkflow } from '../api/useWorkflow';
import { NodePalette } from '../components/editor/NodePalette';
import { WorkflowCanvas } from '../components/editor/WorkflowCanvas';
import { useWorkflowStore } from '../store/workflowStore';
import type { WorkflowEdge, WorkflowNode } from '../types';

function parseCanvasJson(canvasJson: string | null | undefined): {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
} {
  if (!canvasJson) return { nodes: [], edges: [] };
  try {
    return JSON.parse(canvasJson) as { nodes: WorkflowNode[]; edges: WorkflowEdge[] };
  } catch {
    return { nodes: [], edges: [] };
  }
}

export function WorkflowEditorScreen() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  // Read-only "View" mode (?mode=view) — skips autosave and disables the
  // palette so the canvas can be inspected without persisting changes.
  const isView = searchParams.get('mode') === 'view';
  const { data: workflow, isLoading, isError } = useWorkflow(workflowId);
  const saveWorkflow = useSaveWorkflow();

  const initCanvas = useWorkflowStore((s) => s.initCanvas);
  const resetCanvas = useWorkflowStore((s) => s.resetCanvas);
  const isDirty = useWorkflowStore((s) => s.isDirty);
  const setDirty = useWorkflowStore((s) => s.setDirty);

  const { undo, redo, pastStates, futureStates } = useStore(useWorkflowStore.temporal);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Initialise canvas once workflow data arrives.
  useEffect(() => {
    if (!workflow) return;
    const { nodes: savedNodes, edges: savedEdges } = parseCanvasJson(workflow.canvasJson);
    initCanvas(workflow.id, savedNodes, savedEdges, workflow.schemaVersion);
  }, [workflow, initCanvas]);

  // Reset canvas state on unmount.
  useEffect(() => () => resetCanvas(), [resetCanvas]);

  // Warn on unload if dirty.
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) {
        e.preventDefault();
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Debounced autosave — fires 500 ms after isDirty flips true. Reads
  // fresh state inside the timeout to avoid stale closures.
  useEffect(() => {
    if (!isDirty || !workflowId || isView) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const { nodes: n, edges: e, schemaVersion: sv } = useWorkflowStore.getState();
      saveWorkflow.mutate(
        { workflowId, canvasJson: JSON.stringify({ nodes: n, edges: e }), schemaVersion: sv },
        { onSuccess: () => setDirty(false) },
      );
    }, 500);
    return () => clearTimeout(saveTimerRef.current);
  }, [isDirty, workflowId, isView, saveWorkflow.mutate, setDirty]);

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !workflow) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3">
        <p className="text-sm text-gray66">Failed to load workflow.</p>
        <Link to="/workflows" className="text-sm text-primary hover:underline">
          ← Back to Workflows
        </Link>
      </div>
    );
  }

  const canUndo = pastStates.length > 0;
  const canRedo = futureStates.length > 0;

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      {/* Top bar */}
      <header className="flex h-13 flex-shrink-0 items-center gap-3 border-b border-placeholder/40 bg-white px-4">
        <Link
          to="/workflows"
          className="flex items-center gap-1.5 text-sm text-gray66 hover:text-gray30 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Workflows</span>
        </Link>

        <div className="h-4 w-px bg-placeholder/40" />

        <h1 className="flex-1 text-sm font-medium text-gray30 truncate">{workflow.name}</h1>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => undo()}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="flex h-7 w-7 items-center justify-center rounded border border-placeholder/40 text-gray66 hover:border-primary/40 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Undo2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => redo()}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="flex h-7 w-7 items-center justify-center rounded border border-placeholder/40 text-gray66 hover:border-primary/40 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Redo2 className="w-3.5 h-3.5" />
          </button>

          <div className="h-4 w-px bg-placeholder/40" />

          {isView ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-grayEF px-3 py-1 text-xs font-medium text-gray66">
              <Eye className="h-3.5 w-3.5" />
              View only
            </span>
          ) : (
            <div className="flex items-center gap-1.5 text-xs">
              {saveWorkflow.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-gray66" />
                  <span className="text-gray66">Saving…</span>
                </>
              ) : isDirty ? (
                <span className="text-amber-500">Unsaved changes</span>
              ) : (
                <>
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-gray66">Saved</span>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Editor body. In View mode the disabled fieldset locks the node
          palette; combined with autosave being skipped, nothing persists. */}
      <fieldset disabled={isView} className="contents">
        <div className="flex flex-1 min-h-0">
          <NodePalette />
          <WorkflowCanvas />
        </div>
      </fieldset>
    </div>
  );
}
